import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Filter } from 'lucide-react';
import { motion } from 'framer-motion';
import { formatDisplayDate } from '../../utils/dateUtils';
import { baseURL } from '@/config/APIConfig';


const API = baseURL.dashboardApi;

// Fix leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Create custom colored icons for different statuses
const createColoredIcon = (color) => {
    return L.divIcon({
        className: 'custom-marker',
        html: `
      <div style="
        width: 25px;
        height: 25px;
        background-color: ${color};
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 5px rgba(0,0,0,0.3);
      "></div>
    `,
        iconSize: [25, 25],
        iconAnchor: [12, 12],
    });
};

const getMarkerIcon = (status) => {
    switch (status) {
        case 'In Transit':
            return createColoredIcon('#3B82F6'); // blue
        case 'Delayed':
            return createColoredIcon('#EF4444'); // red
        case 'Delivered':
            return createColoredIcon('#10B981'); // green
        default:
            return createColoredIcon('#6B7280'); // gray
    }
};

const ShipmentMap = () => {
    const [shipments, setShipments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedStatus, setSelectedStatus] = useState('all');

    useEffect(() => {
        fetchShipments();
    }, [selectedStatus]);

    const fetchShipments = async () => {
        try {
            const url = selectedStatus === 'all' ? `${API}/shipments` : `${API}/shipments?status=${selectedStatus}`;
            const response = await axios.get(url);
            setShipments(response.data);
        } catch (error) {
            console.error('Error fetching shipments:', error);
        } finally {
            setLoading(false);
        }
    };

    const statusOptions = ['all', 'In Transit', 'Delayed', 'Delivered'];

    return (
        <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-800"
            data-testid="shipment-map"
        >
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">Real-Time Shipment Map</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Track all shipments globally</p>
                </div>

                <div className="flex items-center space-x-2">
                    <Filter className="w-5 h-5 text-slate-400" />
                    <select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        data-testid="status-filter"
                        className="px-4 py-2 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        {statusOptions?.map((status) => (
                            <option key={status} value={status}>
                                {status === 'all' ? 'All Shipments' : status}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="relative h-[500px] rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
                {loading ? (
                    <div className="flex items-center justify-center h-full bg-slate-100 dark:bg-slate-800">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    <MapContainer
                        center={[20, 0]}
                        zoom={2}
                        style={{ height: '100%', width: '100%' }}
                        className="z-0"
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {Array.isArray(shipments) && shipments?.map((shipment) => (
                            <Marker
                                key={shipment.id}
                                position={[shipment.lat, shipment.lng]}
                                icon={getMarkerIcon(shipment.status)}
                            >
                                <Popup>
                                    <div className="p-2">
                                        <div className="font-bold text-sm mb-1">{shipment.container_id}</div>
                                        <div className="text-xs space-y-1">
                                            <div><span className="font-semibold">From:</span> {shipment.origin}</div>
                                            <div><span className="font-semibold">To:</span> {shipment.destination}</div>
                                            <div><span className="font-semibold">Carrier:</span> {shipment.carrier}</div>
                                            <div><span className="font-semibold">Status:</span>
                                                <span className={`ml-1 font-medium ${shipment.status === 'Delivered' ? 'text-green-600' :
                                                    shipment.status === 'Delayed' ? 'text-red-600' :
                                                        'text-blue-600'
                                                    }`}>{shipment.status}</span>
                                            </div>
                                            <div><span className="font-semibold">ETA:</span> {formatDisplayDate(shipment.eta)}</div>
                                        </div>
                                    </div>
                                </Popup>
                            </Marker>
                        ))}
                    </MapContainer>
                )}
            </div>

            <div className="flex items-center justify-center space-x-6 mt-4">
                <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span className="text-sm text-slate-600 dark:text-slate-400">In Transit</span>
                </div>
                <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span className="text-sm text-slate-600 dark:text-slate-400">Delayed</span>
                </div>
                <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-slate-600 dark:text-slate-400">Delivered</span>
                </div>
            </div>
        </motion.div>
    );
};

export default ShipmentMap;